import type { DeviceOption, SaleOrderLine, SaleOrderLineView, ScannedSerialRow } from "../types";

/** Serial khớp dòng đơn theo mã hàng hoặc id hàng (giống trang cũ với loại phiếu không theo serial) */
const lineKeyOf = (itemCode: string | null | undefined, itemId: number | null | undefined) =>
  itemCode ? `code:${itemCode}` : itemId != null ? `id:${itemId}` : "";

/**
 * Tổng số lượng đã quét theo từng dòng đơn. O(số serial + số dòng đơn),
 * thay cho vòng lặp lồng filter trong trang cũ.
 */
export const scannedQuantityByLine = (
  orderLines: SaleOrderLine[],
  rows: ScannedSerialRow[],
): Map<SaleOrderLine, number> => {
  const byCode = new Map<string, number>();
  const byId = new Map<number, number>();
  rows.forEach((row) => {
    const quantity = Number(row.primaryQuantity) || 0;
    if (row.itemCode) byCode.set(row.itemCode, (byCode.get(row.itemCode) ?? 0) + quantity);
    if (row.itemId != null) byId.set(row.itemId, (byId.get(row.itemId) ?? 0) + quantity);
  });

  const result = new Map<SaleOrderLine, number>();
  orderLines.forEach((line) => {
    const quantity =
      (line.itemCode ? byCode.get(line.itemCode) : undefined) ??
      (line.itemId != null ? byId.get(line.itemId) : undefined) ??
      0;
    result.set(line, quantity);
  });
  return result;
};

/** View theo object dòng đơn: số quét không đổi thì trả lại đúng object cũ, để bảng dòng đơn bỏ qua dòng đó */
const lineViewCache = new WeakMap<SaleOrderLine, SaleOrderLineView>();

/** Dòng đơn kèm số đã quét, còn lại, thành tiền. Giá dùng giá bán (soPrice1) cho cả xuất bán và đổi trả */
export const buildOrderLineView = (
  orderLines: SaleOrderLine[],
  rows: ScannedSerialRow[],
): SaleOrderLineView[] => {
  const scanned = scannedQuantityByLine(orderLines, rows);
  return orderLines.map((line) => {
    const scannedQuantity = scanned.get(line) ?? 0;
    const cached = lineViewCache.get(line);
    if (cached && cached.scannedQuantity === scannedQuantity) return cached;
    const orderQuantity = line.primaryQuantity ?? 0;
    const transferred = line.transactionQuantity ?? 0;
    const view: SaleOrderLineView = {
      ...line,
      scannedQuantity,
      remainQuantity: orderQuantity - (transferred + scannedQuantity),
      priceItem: (line.soPrice1 ?? 0) * scannedQuantity,
    };
    lineViewCache.set(line, view);
    return view;
  });
};

/** Có dòng đơn bị quét vượt số lượng cần xuất */
export const hasExceededLine = (lines: SaleOrderLineView[]) =>
  lines.some((line) => line.scannedQuantity > (line.primaryQuantity ?? 0));

/** Các dòng đơn lệch giữa số đã quét và số cần xuất */
export const getVarianceItems = (lines: SaleOrderLineView[]) =>
  lines
    .map((line) => ({
      itemName: line.itemName,
      varianceQuantity: line.scannedQuantity - (line.primaryQuantity ?? 0),
    }))
    .filter((item) => item.varianceQuantity !== 0);

/** Thêm các dòng mới vào thì có dòng đơn nào bị vượt không (kiểm tra trước khi nhận serial) */
export const wouldExceedAfterAdding = (
  orderLines: SaleOrderLine[],
  rows: ScannedSerialRow[],
  newRows: ScannedSerialRow[],
) => hasExceededLine(buildOrderLineView(orderLines, [...rows, ...newRows]));

export const totalScannedQuantity = (rows: ScannedSerialRow[]) =>
  rows.reduce((total, row) => total + (Number(row.primaryQuantity) || 0), 0);

/** Chỉ mục dòng đơn theo khoá, dựng một lần cho mỗi mảng orderLines (LOAD_* luôn thay cả mảng) */
const lineIndexByLines = new WeakMap<SaleOrderLine[], Map<string, SaleOrderLine>>();

/** Dòng đơn của một serial, để giới hạn số lượng khi sửa SL1 với hàng không serial. O(1) thay vì find O(số dòng đơn) */
export const findOrderLineForRow = (orderLines: SaleOrderLine[], row: ScannedSerialRow) => {
  let index = lineIndexByLines.get(orderLines);
  if (!index) {
    index = new Map<string, SaleOrderLine>();
    for (const line of orderLines) {
      const key = lineKeyOf(line.itemCode, line.itemId);
      if (!index.has(key)) index.set(key, line); // giữ dòng đầu tiên như Array.find
    }
    lineIndexByLines.set(orderLines, index);
  }
  return index.get(lineKeyOf(row.itemCode, row.itemId));
};

// --- Bảo hành mở rộng (BHMR) ---

const serialKey = (serial: string | null | undefined) => (serial ?? "").trim();

/**
 * Thiết bị có thể gắn cho một thẻ BHMR: các dòng trên phiếu không phải thẻ, không phải hàng không serial.
 * Mục chọn được đứng trước, sau đó là mục bị khoá kèm lý do; trong mỗi nhóm giữ thứ tự quét.
 */
export const getDeviceOptions = (rows: ScannedSerialRow[], cardSerial: string): DeviceOption[] => {
  const cardKey = serialKey(cardSerial);
  const card = rows.find((row) => serialKey(row.serial) === cardKey);
  const targets = (card?.warrantyTargetItemCodes ?? []).map((code) => serialKey(code)).filter(Boolean);
  const currentDevice = serialKey(card?.linkedDeviceSerial);

  // Serial thiết bị -> serial thẻ khác đang giữ thiết bị đó
  const heldBy = new Map<string, string>();
  rows.forEach((row) => {
    const device = serialKey(row.linkedDeviceSerial);
    const key = serialKey(row.serial);
    if (row.isWarrantyCard && device && key !== cardKey && !heldBy.has(device)) heldBy.set(device, key);
  });

  const options = rows
    .filter((row) => !row.isWarrantyCard && !row.isNoSerial && serialKey(row.serial))
    .map((row): DeviceOption => {
      const key = serialKey(row.serial);
      if (currentDevice && key === currentDevice) return { row, disabled: false, reason: "Đang gắn với thẻ này" };
      const otherCard = heldBy.get(key);
      if (otherCard) return { row, disabled: true, reason: `Đã gắn thẻ ${otherCard}` };
      if (row.hasActiveExtWarranty) return { row, disabled: true, reason: "Đã có gói BHMR còn hiệu lực" };
      if ((targets.length || card?.warrantyTargetsStrict) && !targets.includes(serialKey(row.itemCode))) {
        return { row, disabled: true, reason: "Thẻ không áp dụng cho mã hàng này" };
      }
      return { row, disabled: false, reason: "Chưa có thẻ" };
    });
  return [...options.filter((option) => !option.disabled), ...options.filter((option) => option.disabled)];
};

/**
 * "Thẻ CHƯA gắn còn máy chọn được không" cho cả phiếu: dựng O(n) một lần, mỗi thẻ tra O(số mã áp dụng).
 * Cùng kết quả getDeviceOptions(rows, card.serial).some((o) => !o.disabled) khi thẻ chưa gắn và serial không trùng.
 * Thẻ chưa gắn không giữ máy nào, nên "thẻ khác đang giữ" bằng "mọi thẻ đang giữ".
 */
export const createSelectableDeviceChecker = (rows: ScannedSerialRow[]) => {
  const held = new Set<string>();
  rows.forEach((row) => {
    const device = serialKey(row.linkedDeviceSerial);
    if (row.isWarrantyCard && device) held.add(device);
  });
  const freeCodes = new Set<string>();
  let anyFree = false;
  rows.forEach((row) => {
    const key = serialKey(row.serial);
    if (row.isWarrantyCard || row.isNoSerial || !key || held.has(key) || row.hasActiveExtWarranty) return;
    anyFree = true;
    freeCodes.add(serialKey(row.itemCode));
  });
  return (card: ScannedSerialRow): boolean => {
    const targets = (card.warrantyTargetItemCodes ?? []).map((code) => serialKey(code)).filter(Boolean);
    if (targets.length || card.warrantyTargetsStrict) return targets.some((code) => freeCodes.has(code));
    return anyFree;
  };
};
